// Adapting a programme to how training actually goes (#94).
//
// A finished week is LIGHT when fewer than half its planned days were done,
// or under ~60% of its planned minutes. Two weeks in a row where most
// sessions felt 9-10 out of 10 are HARD. Khronos spots both, asks the user on
// the Programme tab (never changes the plan on its own), and passes the
// answer to the session generator so the next week responds.
//
// session_rating is effort (RPE), never enjoyment.

import { programmeDeloadWeeks, parseScheduledLabel } from "../config/periodisation";
import { isShortFirstWeek } from "./programmeSchedule";

export const LIGHT_DAYS_SHARE = 0.5;
export const LIGHT_MINUTES_SHARE = 0.6;
export const HARD_EFFORT = 9;

const findWeek = (programme, n) =>
  (programme?.weeks || []).find((w) => Number(w.week_number) === Number(n));

const plannedMinutes = (programme, session) =>
  Number(session.target_minutes) > 0
    ? Number(session.target_minutes)
    : Number(programme?.session_minutes) > 0
      ? Number(programme.session_minutes)
      : 0;

/** Days, minutes and effort ratings for one programme week. */
export const weekSummary = (programme, weekNumber) => {
  const sessions = findWeek(programme, weekNumber)?.sessions || [];
  const done = sessions.filter((s) => s.status === "completed");
  return {
    week: Number(weekNumber),
    planned: sessions.length,
    completed: done.length,
    plannedMinutes: sessions.reduce((sum, s) => sum + plannedMinutes(programme, s), 0),
    doneMinutes: done.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0),
    ratings: done.map((s) => Number(s.session_rating)).filter((r) => r >= 1 && r <= 10),
    isDeload: sessions.some((s) => parseScheduledLabel(s.scheduled_label)?.isDeload),
    // #125: a week 1 that started after its Monday holds only the days from
    // the start, so its planned days and minutes already match what's in it.
    partial: Number(weekNumber) === 1 && isShortFirstWeek(programme),
  };
};

// A short first week (#125) is never light: it was planned short.
export const isLightWeek = (summary) => {
  if (!summary || summary.planned === 0 || summary.isDeload || summary.partial) return false;
  if (summary.completed < summary.planned * LIGHT_DAYS_SHARE) return true;
  return summary.plannedMinutes > 0 && summary.doneMinutes < summary.plannedMinutes * LIGHT_MINUTES_SHARE;
};

/** Most rated sessions at effort 9-10, with at least two rated. */
export const isHardWeek = (summary) => {
  if (!summary || summary.isDeload || summary.ratings.length < 2) return false;
  const hard = summary.ratings.filter((r) => r >= HARD_EFFORT).length;
  return hard > summary.ratings.length / 2;
};

const checkinFor = (programme, week) =>
  (programme?.checkins || []).find((c) => Number(c.week_number) === Number(week)) || null;

/** The deload weeks as built: moved ones when stored, else from the labels. */
export const currentDeloads = (programme) => programmeDeloadWeeks(programme);

/**
 * What to ask about at the start of `currentWeek`, or null. Looks at the week
 * just finished; asks once per week (a check-in, whatever the answer, settles
 * it).
 *
 * @returns {null | { type: "light", week, summary, twoInARow, deloadSoon }
 *               | { type: "hard", week, nextDeload }}
 */
export const weekSuggestion = (programme, currentWeek) => {
  const now = Number(currentWeek);
  const last = now - 1;
  if (!programme || last < 1 || checkinFor(programme, last)) return null;
  const deloads = currentDeloads(programme);
  const nextDeload = deloads.find((w) => w >= now) ?? null;

  const summary = weekSummary(programme, last);
  if (isLightWeek(summary)) {
    const before = weekSummary(programme, last - 1);
    return {
      type: "light",
      week: last,
      summary,
      twoInARow: last > 1 && isLightWeek(before),
      // A deload due this week or next can be swapped for the light week.
      deloadSoon: nextDeload != null && nextDeload <= now + 1 ? nextDeload : null,
    };
  }

  if (last > 1 && isHardWeek(summary) && isHardWeek(weekSummary(programme, last - 1))) {
    // Only worth suggesting when the next deload isn't already this week.
    if (nextDeload == null || nextDeload > now) {
      return { type: "hard", week: last, nextDeload };
    }
  }
  return null;
};

/**
 * What a light-week answer leads to. `countAsDeload` is the user's yes to
 * swapping a deload due soon; it is only offered for travelling or busy, since
 * being ill is not recovery.
 *
 * @returns {{ action, move: null | { from, to }, message }}
 */
export const lightWeekOutcome = (suggestion, answer, { countAsDeload = false, repeatWeek = false } = {}) => {
  if (answer === "fine") {
    return { action: "none", move: null, message: null };
  }
  // #92: re-do the week and move the rest of the programme a week later.
  if (repeatWeek) {
    return {
      action: "repeat_week",
      move: null,
      repeatWeek: true,
      message: `Week ${suggestion.week} repeats this week, and the rest of the programme moves a week later.`,
    };
  }
  if (answer === "ill") {
    return {
      action: "ease",
      move: null,
      message: "This week starts easier and repeats last week's targets.",
    };
  }
  if (countAsDeload && suggestion?.deloadSoon) {
    return {
      action: "deload",
      move: { from: suggestion.deloadSoon, to: suggestion.week },
      message: `Week ${suggestion.week} counts as your deload, so week ${suggestion.deloadSoon} is a normal week.`,
    };
  }
  if (suggestion?.twoInARow) {
    return {
      action: "ease",
      move: null,
      message: "Two light weeks in a row, so this week eases you back in with lighter loads.",
    };
  }
  return {
    action: "repeat",
    move: null,
    message: "This week repeats last week's targets instead of moving on.",
  };
};

/** Deload weeks after moving one deload `from` -> `to`. */
export const movedDeloads = (programme, { from, to }) => {
  const set = new Set(currentDeloads(programme));
  set.delete(Number(from));
  set.add(Number(to));
  return [...set].sort((a, b) => a - b);
};

/** "1 of 4 sessions, 35 of 160 min". */
export const describeWeek = (summary) => {
  const sessions = `${summary.completed} of ${summary.planned} ${summary.planned === 1 ? "session" : "sessions"}`;
  return summary.plannedMinutes > 0
    ? `${sessions}, ${summary.doneMinutes} of ${summary.plannedMinutes} min`
    : sessions;
};

/**
 * Guidance for the session generator about the week before `weekNumber`:
 * the user's check-in when there is one, otherwise what the numbers say.
 * Null when last week went normally.
 */
export const lastWeekGuidance = (programme, weekNumber) => {
  const last = Number(weekNumber) - 1;
  if (last < 1) return null;
  const checkin = checkinFor(programme, last);
  const summary = weekSummary(programme, last);
  const light = isLightWeek(summary);
  const twoInARow = light && last > 1 && isLightWeek(weekSummary(programme, last - 1));

  // A repeated week (#92) carries its own check-in: after illness it still
  // starts easier.
  const repeated = checkinFor(programme, Number(weekNumber));
  if (repeated?.action === "repeat_week" && repeated.answer === "ill") {
    return `RETURNING FROM ILLNESS: this week repeats the one the user missed while ill. Make this session easier than usual — about 70-80% of normal working sets, moderate loads, nothing near failure, short and easy on cardio.`;
  }
  if (checkin?.action === "none" || (checkin && checkin.answer === "fine")) return null;
  if (checkin?.answer === "ill") {
    return `RETURNING FROM ILLNESS: the user was ill last week (week ${last}). Make this session easier than usual — about 70-80% of normal working sets, moderate loads, nothing near failure, short and easy on cardio — then rebuild by repeating last week's intended targets rather than progressing.`;
  }
  if (checkin?.action === "deload") {
    return `Last week (week ${last}) was light and counts as the user's recovery week, so progress normally from the last full week.`;
  }
  if (checkin?.action === "deload_forward") return null; // this week is the deload
  if (twoInARow || checkin?.action === "ease") {
    return `EASE BACK IN: the last two weeks were light (${describeWeek(summary)} last week). Use lighter loads (about 80-90% of the last full week's), one fewer working set on the main lifts, and keep cardio easy.`;
  }
  if (light || checkin?.action === "repeat") {
    return `LAST WEEK WAS LIGHT (${describeWeek(summary)}). Repeat last week's intended targets for this slot rather than progressing.`;
  }
  return null;
};
